// Canonical property field definitions matching the spec

/** Maps MLS listing status values to canonical CW listing status values.
 *  cw_listing_status is always derived from this — never entered manually by agents.
 *  INCOMPLETE is the sole CW-only status (set via "Save Incomplete" button).
 */
export const MLS_TO_CW_STATUS_MAP = {
  "Active": "ACTIVE",
  "Coming Soon": "ACTIVE",
  "Active Under Contract": "ACTIVE",
  "Sold": "SOLD",
  "Rented": "RENTED",
  "Closed": "SOLD",
  "Temp Off-Market": "OFF MARKET",
  // A pending listing is still on the market until it closes, so CW keeps it
  // ACTIVE. This must stay in step with the backend's copy of this map in
  // utils/propertyConstants.js, which is what actually decides the stored value —
  // when the two disagreed, the form showed PENDING and the save wrote ACTIVE.
  "Pending": "ACTIVE",
  "Cancelled": "CANCELLED",
  "Withdrawn": "WITHDRAWN",
  "Expired": "EXPIRED",
};

/** Returns the CW status derived from an MLS status string, or null if unrecognised.
 *
 *  Pass the listing type with it: both MLSs close a lease and a sale with the
 *  same word, so "Closed" is Rented on a rental and Sold on a sale. Mirrors
 *  deriveCwStatusFromMls in the backend's utils/propertyConstants.js, which is
 *  what actually decides the stored value.
 */
export function deriveCwStatusFromMls(mlsStatus, listingType) {
  if (!mlsStatus) return null;
  const status = String(mlsStatus).trim();
  if (status.toLowerCase() === "closed" && arguments.length > 1) {
    return String(listingType || "").toLowerCase().includes("rental") ? "RENTED" : "SOLD";
  }
  return MLS_TO_CW_STATUS_MAP[status] ?? null;
}

/**
 * Total bathrooms for a listing pulled from the MLS.
 *
 * CW's `bathrooms` column counts every bathroom including the halves, with
 * `half_bathrooms` recording how many of them are halves. The MLS's
 * BathroomsFull is full-only, so assigning it here understates a home with a
 * half bath by one. BathroomsTotalInteger is the field that means the same
 * thing as ours, but Miami leaves it null on exactly the listings that have a
 * half bath, so fall back to adding the two together.
 */
export function mlsTotalBathrooms(property) {
  // Number(null) is 0, not NaN, so an absent field has to be rejected before the
  // conversion — otherwise Miami's null BathroomsTotalInteger reads as zero baths
  // on exactly the listings this helper exists to get right.
  const num = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const total = num(property?.BathroomsTotalInteger);
  if (total !== null) return total;
  const full = num(property?.BathroomsFull);
  if (full === null) return null;
  return full + (num(property?.BathroomsHalf) ?? 0);
}

export const LISTING_STATUSES = [
  "ACTIVE",
  "SOLD",
  "RENTED",
  "PENDING",
  "OFF MARKET",
  "CANCELLED",
  "WITHDRAWN",
  "EXPIRED",
  "INCOMPLETE",
];
export const MLS_LISTING_STATUS_OPTIONS = [
  "Active",
  "Coming Soon",
  "Active Under Contract",
  "Sold",
  "Rented",
  "Closed",
  "Temp Off-Market",
  "Pending",
  "Cancelled",
  "Withdrawn",
  "Expired",
];

export const DYNAMIC_STATUSES = ["ACTIVE", "PENDING", "OFF MARKET", "INCOMPLETE"];
export const FINAL_STATUSES = ["SOLD", "RENTED", "CANCELLED", "WITHDRAWN", "EXPIRED"];

// Statuses where the date is auto-set by system (not agent-entered)
export const SYSTEM_DATE_STATUSES = ["INCOMPLETE"];

export const LISTING_TYPES = [
  { value: "Residential Sale", label: "Residential Sale" },
  { value: "Residential Rental", label: "Residential Rental" },
];

export const HOME_TYPES = [
  { value: "SFH", label: "Single Family Home" },
  { value: "Condo", label: "Condominium" },
  { value: "TH", label: "Townhouse" },
  { value: "Villa", label: "Villa" },
  { value: "CoachHome", label: "Coach Home" },
];

// Home types that should show the UNIT # field
export const UNIT_HOME_TYPES = ["Condo", "TH", "Villa"];

export const COMPASS_OPTIONS = ["None", "E", "W", "S", "N", "NE", "NW", "SE", "SW"];

export const RCR_YES_NO_OPTIONS = ["YES", "NO", "UKN"];
export const YES_NO_OPTIONS = ["YES", "NO"];
export const HOMESTEAD_OPTIONS = ["FULL", "PARTIL", "NONE"];

export const PRIVATE_POOL_OPTIONS = ["Yes", "No", "Unknown"];
export const COMMUNITY_POOL_OPTIONS = ["Yes", "No", "Unknown"];
export const WATER_VIEW_OPTIONS = ["YES", "NO", "UKN"];
export const FURNITURE_OPTIONS = ["None", "Part", "Fully", "UKN"];

export const PETS_OPTIONS = ["Yes", "No", "UKN"];

export const GARAGE_OPTIONS = ["0", "1-car", "2-cars", "3-cars or more", "Unknown"];

export const RENTAL_TYPE_OPTIONS = ["Annual (1 Year or More)", "Monthly (1 to 11 Months)", "Short Term (less than 1 month)", "UNKNOWN"];

export const STATUS_DATE_MAP = {
  ACTIVE: "date_became_active",
  SOLD: "date_became_sold",
  RENTED: "date_became_rented",
  PENDING: "date_became_pending",
  "OFF MARKET": "date_became_off_market",
  INCOMPLETE: "date_became_incomplete",
  CANCELLED: "date_became_cancelled",
  WITHDRAWN: "date_became_withdrawn",
  EXPIRED: "date_became_expired",
};

// Red warning text for each status
export const STATUS_WARNING_TEXT = {
  SOLD: "\"SOLD\" is a permanent/final state, meaning that we can update the information in this record in the future but we cannot change the \"SOLD\" status to another listing status.",
  RENTED: "\"RENTED\" is a permanent/final state, meaning that we can update the information in this record in the future but we cannot change the \"RENTED\" status to another listing status.",
  "OFF MARKET": "Enter the date the listing status became OFF MARKET the last time in the MLS. This is a temporary status for ACTIVE OR PENDING listings that are taken off the market for a short time. Eventually, the listing will be moved to another state (i.e., ACTIVE, PENDING, SOLD, RENTED, CANCELLED, etc.).",
  INCOMPLETE: "A system generated date. The date shows the listing status becomes INCOMPLETE. This status will only be used when we are entering a new listing into our CW database. You can come back and complete the listing later and change its status to a proper status like ACTIVE, SOLD, RENTED, CANCELLED, etc.",
  CANCELLED: "Enter the date the listing status became CANCELLED in the MLS.",
  WITHDRAWN: "Enter the date the listing status became WITHDRAWN in the MLS.",
  EXPIRED: "Enter the date the listing status reached EXPIRATION in the MLS.",
};

// Display labels for status date history
export const STATUS_DATE_LABELS = {
  date_became_active: "Latest active date",
  date_became_sold: "MLS sold date",
  date_became_rented: "Rented date",
  date_became_pending: "Latest pending date",
  date_became_off_market: "Latest off market date",
  date_became_incomplete: "Latest incomplete date",
  date_became_cancelled: "Cancelled date",
  date_became_withdrawn: "Withdrawn date",
  date_became_expired: "Expiration date",
};

// County Property Appraiser Links
export const COUNTY_PROPERTY_LINKS = {
  "Broward County": "https://bcpa.net/RecMenu.asp",
  "Martin County": "https://www.pa.martin.fl.us/app/search/real-property",
  "Miami-Dade County": "https://apps.miamidadepa.gov/PropertySearch/#/",
  "Monroe County": "https://qpublic.schneidercorp.com/Application.aspx?AppID=605&LayerID=9946&PageTypeID=2&PageID=4381",
  "Palm Beach County": "https://pbcpao.gov/AdvSearch/AdvanceSearch",
  "St Lucie County": "https://apps.paslc.gov/property-search/real-estate/basic-site-address",
};

/**
 * Shared validation function for property forms.
 * Returns an array of error strings (empty = valid).
 */
export function validatePropertyForm(formData) {
  const errors = [];

  // Required fields
  if (!formData.subdivision_id) errors.push("Subdivision is required");
  if (!formData.street_name) errors.push("Street Name is required");
  if (!formData.city) errors.push("City is required");
  if (!formData.state) errors.push("State is required");
  if (!formData.zip_code) errors.push("Zip Code is required");
  if (!formData.county_name) errors.push("County is required");

  // RCR fields are conditional on listing type + status
  const lt = formData.listing_type || "";
  const ls = formData.cw_listing_status || "";
  if (lt === "Residential Sale" && ls === "ACTIVE" && !formData.rcr_seller) errors.push("RCR Seller is required");
  if (lt === "Residential Sale" && ls === "SOLD" && !formData.rcr_buyer) errors.push("RCR Buyer is required");
  if (lt === "Residential Rental" && ls === "ACTIVE" && !formData.rcr_landlord) errors.push("RCR Landlord is required");
  if (lt === "Residential Rental" && ls === "RENTED" && !formData.rcr_tenant) errors.push("RCR Tenant is required");
  if (!formData.deviation_from_original_data) {
    errors.push("Deviation From Original Data is required");
  }
  if (["SOLD", "RENTED"].includes(ls) && !formData.anomaly) {
    errors.push("Anomaly is required when status is SOLD or RENTED");
  }

  // Zip code format
  if (formData.zip_code && !/^\d{5}$/.test(formData.zip_code)) {
    errors.push("Zip Code must be exactly 5 digits");
  }

  // Year built bounds
  const yb = formData.year_built;
  if (yb !== "" && yb !== null && yb !== undefined) {
    const ybNum = Number(yb);
    if (isNaN(ybNum) || String(yb).length !== 4 || ybNum < 1800 || ybNum > 2100) {
      errors.push("Year Built must be a valid 4-digit year between 1800 and 2100");
    }
  }

  // Price bounds (max 10 digits, positive)
  const priceFields = [
    { key: "price_or_rent", label: "Price/Rent" },
    { key: "sold_price", label: "Sold Price" },
    { key: "rented_price", label: "Rented Price" },
  ];
  priceFields.forEach(({ key, label }) => {
    const val = formData[key];
    if (val !== "" && val !== null && val !== undefined) {
      const num = Number(val);
      if (isNaN(num) || num < 0 || num > 9999999999) {
        errors.push(`${label} must be a positive number with at most 10 digits`);
      }
    }
  });

  // Bedrooms / bathrooms bounds (0-99)
  const countFields = [
    { key: "bedrooms", label: "Bedrooms" },
    { key: "bathrooms", label: "Total Bathrooms" },
    { key: "half_bathrooms", label: "Half Bathrooms" },
  ];
  countFields.forEach(({ key, label }) => {
    const val = formData[key];
    if (val !== "" && val !== null && val !== undefined) {
      const num = Number(val);
      if (isNaN(num) || num < 0 || num > 99) {
        errors.push(`${label} must be between 0 and 99`);
      }
    }
  });

  const mlsStatusDateRequirements = {
    PENDING: { key: "date_became_pending", label: "Pending Date" },
    "OFF MARKET": { key: "date_became_off_market", label: "Off Market Date" },
    CANCELLED: { key: "date_became_cancelled", label: "Cancelled Date" },
    WITHDRAWN: { key: "date_became_withdrawn", label: "Withdrawn Date" },
    EXPIRED: { key: "date_became_expired", label: "Expiration Date" },
  };
  const statusDateRequirement = mlsStatusDateRequirements[ls];
  if (statusDateRequirement && !formData[statusDateRequirement.key]) {
    errors.push(`${statusDateRequirement.label} (From MLS) is required when status is ${ls}`);
  }

  return errors;
}

// Empty form data template for all modes
export const EMPTY_FORM_DATA = {
  // RCR fields (mandatory first questions)
  rcr_seller: "",
  rcr_landlord: "",
  rcr_buyer: "",
  rcr_tenant: "",

  // Subdivision
  subdivision_id: "",

  // Listing info
  listing_type: "",
  cw_listing_status: "ACTIVE",
  mls_listing_status: "",
  mls_number: "",
  county_property_id: "",

  // Address
  street_number: "", // BUILDING NO
  compass_direction: "",
  street_name: "",
  post_street_direction: "",
  unit_number: "",
  city: "",
  state: "FL",
  zip_code: "",
  county_name: "",
  short_address: "",

  // Property details
  home_type: "",
  bedrooms: "",
  bathrooms: "",
  half_bathrooms: "",
  sf_under_ac: "",
  year_built: "",
  private_pool: "",
  community_pool: "",
  garage: "",
  water_view: "",
  furniture: "",
  pets: "",
  homestead: "",

  // Links
  mls_link: "",
  county_property_link: "",
  cw_private_notes: "",
  cw_public_notes: "",
  deviation_from_original_data: "",
  anomaly: "",

  // Pricing - Sale
  price_or_rent: "",
  original_asking_price: "",
  mls_sold_price: "",
  previous_sold_price: "",
  previous_sold_date: "",

  // Pricing - Rent
  rental_type: "",
  available_date: "",
  rented_price: "",
  original_asking_rent: "",
  furnished_annual: "",
  furnished_seasonal: "",
  furnished_off_season: "",
  unfurnished_annual: "",
  unfurnished_seasonal: "",
  unfurnished_off_season: "",

  // Computed / derived
  dom: "",
  real_estate_tax: "",
  county_assessed_value: "",

  // Status dates
  date_became_active: "",
  date_became_sold: "",
  date_became_rented: "",
  date_became_pending: "",
  date_became_off_market: "",
  date_became_incomplete: "",
  date_became_cancelled: "",
  date_became_withdrawn: "",
  date_became_expired: "",
};
